import {
  CampaignCurrency,
  DisplayCurrency,
  ValueFirstAuthMode,
  ValueFirstBrand,
  normalizeDomain,
} from '../../onboarding-value-first/services/value-first-onboarding.types';

export type AgencyStep = 'website' | 'manual' | 'found' | 'brands' | 'account' | 'code' | 'welcome';

export type AgencyBrandStatus = 'loading' | 'ready' | 'failed';

export interface AgencyRepresentedBrand {
  id: string;
  name: string;
  domain: string;
  logoUrl?: string;
  bannerUrl?: string;
  description?: string;
  status: AgencyBrandStatus;
}

export interface AgencyCreatedBrand {
  id: string;
  name: string;
  domain: string;
  logoUrl?: string;
}

export interface AgencyDraft {
  step: AgencyStep;
  url: string;
  agency: ValueFirstBrand | null;
  fromLookup: boolean;
  currency: CampaignCurrency | null;
  brandUrl: string;
  brands: AgencyRepresentedBrand[];
  email: string;
  authMode: ValueFirstAuthMode;
  firstName: string;
  lastName: string;
  pick: number;
  createdBrands: AgencyCreatedBrand[];
}

export const AGENCY_STORAGE_KEY = 'shout-value-first-agency';
export const AGENCY_VALUE_FIRST_VARIANT = 'agency_value_first';
export const AGENCY_BRAND_CAP = 3;

export const AGENCY_TRIAL_LINE: Record<DisplayCurrency, string> = {
  GBP: '£449/month (inc. VAT) after your trial. 3 brands included.',
  USD: '$599/month after your trial. 3 brands included.',
};

export function emptyAgencyDraft(): AgencyDraft {
  return {
    step: 'website',
    url: '',
    agency: null,
    fromLookup: false,
    currency: null,
    brandUrl: '',
    brands: [],
    email: '',
    authMode: 'email',
    firstName: '',
    lastName: '',
    pick: 0,
    createdBrands: [],
  };
}

export function agencyWebsiteError(raw: string): string {
  const domain = normalizeDomain(raw);
  if (!domain) {
    return "Enter your agency's website to continue.";
  }
  if (!domain.includes('.')) {
    return 'Enter a full website, like youragency.com.';
  }
  return '';
}

export function representedBrandError(raw: string): string {
  const domain = normalizeDomain(raw);
  if (!domain || !domain.includes('.')) {
    return "Enter the brand's website, like brand.com.";
  }
  return '';
}

export function agencyProgress(step: AgencyStep): 1 | 2 | 3 | null {
  switch (step) {
    case 'website':
    case 'manual':
    case 'found':
      return 1;
    case 'brands':
      return 2;
    case 'account':
    case 'code':
      return 3;
    default:
      return null;
  }
}

export function agencyPreviousStep(draft: AgencyDraft): AgencyStep | null {
  switch (draft.step) {
    case 'manual':
    case 'found':
      return 'website';
    case 'brands':
      return draft.fromLookup ? 'found' : 'manual';
    case 'account':
      return 'brands';
    case 'code':
      return 'account';
    default:
      return null;
  }
}

/** Code entry needs the in-memory password, which a refresh cannot restore. */
export function agencyStepAfterRefresh(draft: AgencyDraft): AgencyStep {
  if (draft.step === 'code') {
    return 'account';
  }
  return draft.step;
}

export function readAgencyDraft(storage: Storage | undefined): AgencyDraft | null {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(AGENCY_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<AgencyDraft>;
    const base = emptyAgencyDraft();
    return {
      ...base,
      ...parsed,
      agency: parsed.agency ?? null,
      fromLookup: parsed.fromLookup === true,
      brands: Array.isArray(parsed.brands) ? parsed.brands.filter(isRepresentedBrand) : [],
      createdBrands: Array.isArray(parsed.createdBrands) ? parsed.createdBrands.filter(isCreatedBrand) : [],
      authMode: parsed.authMode === 'google' ? 'google' : 'email',
      pick: typeof parsed.pick === 'number' && parsed.pick >= 0 ? parsed.pick : 0,
    };
  } catch {
    return null;
  }
}

function isRepresentedBrand(value: unknown): value is AgencyRepresentedBrand {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const brand = value as Partial<AgencyRepresentedBrand>;
  return typeof brand.id === 'string'
    && typeof brand.name === 'string'
    && typeof brand.domain === 'string'
    && (brand.status === 'loading' || brand.status === 'ready' || brand.status === 'failed');
}

function isCreatedBrand(value: unknown): value is AgencyCreatedBrand {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const brand = value as Partial<AgencyCreatedBrand>;
  return typeof brand.id === 'string' && typeof brand.name === 'string' && typeof brand.domain === 'string';
}
